import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router';
import { API_URL, LOCATION_ID } from '..';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

const EditStockLayer = () => {
  const { id } = useParams();
  const [formValues, setFormValues] = useState({
    title: '',
    sellPrice: 0,
    costPrice: 0,
    quantity: 0,
    description: '',
    locationId: LOCATION_ID,
    // ── GHL / Inventory fields ──────────────────────────────
    sku: '',
    color:'',
    productName: '',
    itemName: '',
    trackInventory: true,
    allowOutOfStockPurchases: false,
    image: '',
    ghlProductId: '',
    ghlId: '',
  });

  const [message, setMessage] = useState({ status: false, reason: '', desc: '' });
  const [colorList, setColorList] = useState([]);

  const quillRef = useRef(null);
  const quillInstance = useRef(null);

  // ✅ Fetch Stock Details
  const fetchStock = async () => {
    try {
      const response = await fetch(`${API_URL}/api/invoice/stock/fetch-single/${id}`);
      const data = await response.json();
      if (response.ok) {
        setFormValues({
          title: data.stock.title || '',
          sellPrice: data.stock.sellPrice || 0,
          costPrice: data.stock.costPrice || 0,
          quantity: data.stock.quantity || 0,
          description: data.stock.description || '',
          locationId: data.stock.locationId || LOCATION_ID,
          sku: data.stock.sku || '',
          color: data.stock.color || '',
          productName: data.stock.productName || '',
          itemName: data.stock.itemName || '',
          trackInventory: data.stock.trackInventory !== false,
          allowOutOfStockPurchases: data.stock.allowOutOfStockPurchases || false,
          image: data.stock.image || '',
          ghlProductId: data.stock.ghlProductId || '',
          ghlId: data.stock.ghlId || '',
        });
      } else {
        throw new Error(data.message || 'Failed to fetch stock');
      }
    } catch (error) {
      setMessage({ status: true, reason: 'error', desc: `Failed to fetch stock: ${error.message}` });
    }
  };

  useEffect(() => {
    fetchStock();
  }, [id]);

  useEffect(() => {
    const fetchColors = async () => {
      try {
        const res = await fetch(`${API_URL}/api/color`);
        const data = await res.json();
        setColorList(data);
      } catch (error) {
        console.error('Failed to fetch colors:', error);
      }
    };
    fetchColors();
  }, []);

  // ✅ Initialize Quill
  useEffect(() => {
    if (quillRef.current && !quillInstance.current) {
      quillInstance.current = new Quill(quillRef.current, {
        theme: 'snow',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ align: '' }, { align: 'center' }, { align: 'right' }],
          ],
        },
        placeholder: 'Enter stock description',
      });

      quillInstance.current.root.setAttribute('dir', 'ltr');
      quillInstance.current.root.setAttribute('lang', 'en');

      quillInstance.current.on('text-change', () => {
        const content = quillInstance.current.root.innerHTML;
        setFormValues((prev) => ({ ...prev, description: content }));
      });
    }

    return () => {
      if (quillInstance.current) {
        quillInstance.current.off('text-change');
        quillInstance.current = null;
      }
    };
  }, []);

  // ✅ Populate description in Quill after fetch
  useEffect(() => {
    if (quillInstance.current && formValues.description) {
      if (quillInstance.current.root.innerHTML !== formValues.description) {
        quillInstance.current.root.innerHTML = formValues.description;
      }
    }
  }, [formValues.description]);

  const handleChange = (field, value) =>
    setFormValues((prev) => ({ ...prev, [field]: value }));

  // ✅ Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user) {
      setMessage({ status: true, reason: 'error', desc: 'User not authenticated. Please sign in.' });
      return;
    }

    // Validation
    if (!formValues.title)
      return setMessage({ status: true, reason: 'error', desc: 'Title is required' });
    // if (!formValues.costPrice || formValues.costPrice <= 0)
    //   return setMessage({ status: true, reason: 'error', desc: 'Buy Price must be greater than 0' });
    // if (!formValues.sellPrice || formValues.sellPrice <= 0)
    //   return setMessage({ status: true, reason: 'error', desc: 'Selling Price must be greater than 0' });
    if (!formValues.quantity || formValues.quantity < 0)
      return setMessage({ status: true, reason: 'error', desc: 'Quantity cannot be negative' });
    if (!formValues.description || formValues.description === '<p><br></p>')
      return setMessage({ status: true, reason: 'error', desc: 'Description is required' });

    try {
      const response = await fetch(`${API_URL}/api/invoice/stock/update-single/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formValues),
      });
      const data = await response.json();

      if (response.ok) {
        setMessage({ status: true, reason: 'success', desc: 'Stock updated successfully' });
      } else {
        throw new Error(data.message || 'Failed to update stock');
      }
    } catch (error) {
      setMessage({ status: true, reason: 'error', desc: `Failed to update stock: ${error.message}` });
    }
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-body p-24">
        <div className="row justify-content-center">
          <div className="col-xxl-8 col-xl-10 col-lg-12">
            <div className="card border">
              <div className="card-header border-bottom py-16 px-24">
                <h6 className="text-lg fw-semibold mb-0">Edit Stock Item</h6>
                <p className="text-secondary-light text-sm mb-0 mt-4">
                  Update the information for this inventory item.
                </p>
              </div>

              <div className="card-body p-24">
                {/* Alerts */}
                {message.status && (
                  <div
                    className={`alert alert-${message.reason === 'error' ? 'danger' : 'success'} alert-dismissible fade show mb-20`}
                    role="alert"
                  >
                    {message.desc}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setMessage({ ...message, status: false })}
                      aria-label="Close"
                    ></button>
                  </div>
                )}

                <form onSubmit={handleSubmit}>

                  {/* ── Section: Basic Info ─────────────────────────── */}
                  <div className="mb-16">
                    <h6 className="text-sm fw-bold text-primary-600 text-uppercase mb-12 pb-8 border-bottom">
                      📦 Basic Information
                    </h6>
                  </div>

                  <div className="row">
                    {/* Stock Title */}
                    <div className="col-md-6 mb-20">
                      <label htmlFor="title" className="form-label fw-semibold text-primary-light text-sm mb-8">
                        Title <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control radius-8"
                        id="title"
                        value={formValues.title}
                        onChange={(e) => handleChange('title', e.target.value)}
                        placeholder="Short stock title..."
                      />
                    </div>

                    {/* SKU */}
                    <div className="col-md-6 mb-20">
                      <label htmlFor="sku" className="form-label fw-semibold text-primary-light text-sm mb-8">
                        SKU
                      </label>
                      <input
                        type="text"
                        className="form-control radius-8"
                        id="sku"
                        value={formValues.sku}
                        onChange={(e) => handleChange('sku', e.target.value)}
                        placeholder="e.g. CG-PVC-UNARM-300MM2-3-C"
                      />
                    </div>

                    {/* Product Name */}
                    <div className="col-md-6 mb-20">
                      <label htmlFor="productName" className="form-label fw-semibold text-primary-light text-sm mb-8">
                        Product Name
                        <span className="text-secondary-light text-xs ms-1">(Full product name)</span>
                      </label>
                      <input
                        type="text"
                        className="form-control radius-8"
                        id="productName"
                        value={formValues.productName}
                        onChange={(e) => handleChange('productName', e.target.value)}
                        placeholder="e.g. CopperGat PVC Power Cable - 3/C PVC/PVC..."
                      />
                    </div>
                      <div className="col-md-6 mb-20">
                      <label htmlFor="color" className="form-label fw-semibold text-primary-light text-sm mb-8">
                        COLOR
                      </label>
                      <select
                        className="form-select radius-8"
                        id="color"
                        value={formValues.color}
                        onChange={(e) => handleChange('color', e.target.value)}
                      >
                        <option value="">Select Color</option>
                        {colorList.map((color) => (
                          <option key={color.id} value={color.title}>{color.title}</option>
                        ))}
                      </select>
                    </div>

                    {/* Item / Variant Name */}
                    <div className="col-md-6 mb-20">
                      <label htmlFor="itemName" className="form-label fw-semibold text-primary-light text-sm mb-8">
                        Item / Variant Name
                        <span className="text-secondary-light text-xs ms-1">(Variant description)</span>
                      </label>
                      <input
                        type="text"
                        className="form-control radius-8"
                        id="itemName"
                        value={formValues.itemName}
                        onChange={(e) => handleChange('itemName', e.target.value)}
                        placeholder='e.g. 3/C PVC/PVC / 300 mm² (61/0.103") / Un-Armoured'
                      />
                    </div>

                    {/* Image URL */}
                    <div className="col-md-12 mb-20">
                      <label htmlFor="image" className="form-label fw-semibold text-primary-light text-sm mb-8">
                        Image URL
                      </label>
                      <input
                        type="text"
                        className="form-control radius-8"
                        id="image"
                        value={formValues.image}
                        onChange={(e) => handleChange('image', e.target.value)}
                        placeholder="https://..."
                      />
                    </div>
                  </div>

                  {/* ── Section: GHL IDs ────────────────────────────── */}
                  <div className="mb-16 mt-8">
                    <h6 className="text-sm fw-bold text-primary-600 text-uppercase mb-12 pb-8 border-bottom">
                      🔗 GHL Reference IDs
                    </h6>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-20">
                      <label htmlFor="ghlProductId" className="form-label fw-semibold text-primary-light text-sm mb-8">
                        GHL Product ID
                        <span className="text-secondary-light text-xs ms-1">("product" field)</span>
                      </label>
                      <input
                        type="text"
                        className="form-control radius-8"
                        id="ghlProductId"
                        value={formValues.ghlProductId}
                        onChange={(e) => handleChange('ghlProductId', e.target.value)}
                        placeholder="69fbc9f67c5ebf357deb12d4"
                      />
                    </div>

                    <div className="col-md-6 mb-20">
                      <label htmlFor="ghlId" className="form-label fw-semibold text-primary-light text-sm mb-8">
                        GHL Item ID
                        <span className="text-secondary-light text-xs ms-1">("_id" field)</span>
                      </label>
                      <input
                        type="text"
                        className="form-control radius-8"
                        id="ghlId"
                        value={formValues.ghlId}
                        onChange={(e) => handleChange('ghlId', e.target.value)}
                        placeholder="69fbc9f77c5ebf0e44eb1338"
                      />
                    </div>
                  </div>

                  {/* ── Section: Pricing & Quantity ─────────────────── */}
                  <div className="mb-16 mt-8">
                    <h6 className="text-sm fw-bold text-primary-600 text-uppercase mb-12 pb-8 border-bottom">
                      💰 Pricing & Quantity
                    </h6>
                  </div>

                  <div className="row">
                    <div className="col-md-4 mb-20">
                      <label htmlFor="buy" className="form-label fw-semibold text-primary-light text-sm mb-8">
                        Buy Price <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control radius-8"
                        id="buy"
                        value={formValues.costPrice}
                        onChange={(e) => handleChange('costPrice', e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="col-md-4 mb-20">
                      <label htmlFor="sell" className="form-label fw-semibold text-primary-light text-sm mb-8">
                        Selling Price <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control radius-8"
                        id="sell"
                        value={formValues.sellPrice}
                        onChange={(e) => handleChange('sellPrice', e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="col-md-4 mb-20">
                      <label htmlFor="qty" className="form-label fw-semibold text-primary-light text-sm mb-8">
                        Quantity <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control radius-8"
                        id="qty"
                        value={formValues.quantity}
                        onChange={(e) => handleChange('quantity', e.target.value)}
                        min="0"
                        placeholder="Enter Quantity"
                      />
                    </div>
                  </div>

                  {/* ── Section: Inventory Settings ─────────────────── */}
                  <div className="mb-16 mt-8">
                    <h6 className="text-sm fw-bold text-primary-600 text-uppercase mb-12 pb-8 border-bottom">
                      ⚙️ Inventory Settings
                    </h6>
                  </div>

                  <div className="row mb-20">
                    <div className="col-md-6">
                      <div className="d-flex align-items-center justify-content-between p-16 border radius-8 bg-base">
                        <div>
                          <p className="fw-semibold text-primary-light text-sm mb-2">Track Inventory</p>
                          <p className="text-secondary-light text-xs mb-0">
                            Monitor stock levels and get low-stock alerts
                          </p>
                        </div>
                        <div className="form-check form-switch ms-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="trackInventory"
                            checked={formValues.trackInventory}
                            onChange={(e) => handleChange('trackInventory', e.target.checked)}
                            style={{ width: '2.5rem', height: '1.25rem', cursor: 'pointer' }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="d-flex align-items-center justify-content-between p-16 border radius-8 bg-base">
                        <div>
                          <p className="fw-semibold text-primary-light text-sm mb-2">Allow Out-of-Stock Purchases</p>
                          <p className="text-secondary-light text-xs mb-0">
                            Customers can still purchase even if stock is 0
                          </p>
                        </div>
                        <div className="form-check form-switch ms-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="allowOutOfStockPurchases"
                            checked={formValues.allowOutOfStockPurchases}
                            onChange={(e) => handleChange('allowOutOfStockPurchases', e.target.checked)}
                            style={{ width: '2.5rem', height: '1.25rem', cursor: 'pointer' }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── Section: Description ────────────────────────── */}
                  <div className="mb-16 mt-8">
                    <h6 className="text-sm fw-bold text-primary-600 text-uppercase mb-12 pb-8 border-bottom">
                      📝 Description
                    </h6>
                  </div>

                  <div className="mb-20">
                    <label htmlFor="description" className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Description <span className="text-danger-600">*</span>
                    </label>
                    <div
                      ref={quillRef}
                      className={`ql-container ${message.reason === 'error' &&
                        (!formValues.description || formValues.description === '<p><br></p>')
                        ? 'is-invalid'
                        : ''
                        }`}
                    />
                    <div className="form-text">
                      Provide a detailed description of the stock item (supports bold, bullet points, etc.).
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="d-flex align-items-center justify-content-end gap-3 pt-16 border-top">
                    <button
                      type="submit"
                      className="btn btn-primary border border-primary-400 text-md px-48 py-11 radius-8"
                    >
                      Update Stock
                    </button>
                  </div>

                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditStockLayer;
